import { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/** Marks the portaled listbox so outside-click logic can find it without relying on ref timing. */
const SEARCHABLE_MULTISELECT_MENU_SELECTOR = '[data-searchable-multiselect-menu]';

export interface SearchableMultiSelectOption {
  value: string;
  label: string;
}

export interface SearchableMultiSelectProps {
  options: SearchableMultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export function SearchableMultiSelect({
  options,
  values,
  onChange,
  placeholder = 'Search tenants…',
  disabled = false,
  className = '',
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: Readonly<SearchableMultiSelectProps>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  /** Trigger + chips (not the portaled listbox). */
  const rootRef = useRef<HTMLDivElement>(null);
  /** Portaled dropdown; must be included in outside-click checks. */
  const menuRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const valueSet = useMemo(() => new Set(values), [values]);

  /** Latest selection for toggles (avoids stale closure if parent batches updates). */
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const updateMenuPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setMenuRect({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  const closeDropdown = useCallback(() => {
    setSearchTerm('');
    setIsOpen(false);
    setMenuRect(null);
  }, []);

  /** Portaled menu lives outside `rootRef` in the DOM; use data attr + composedPath so outside-click ignores menu hits (React stopPropagation on ul is not reliable vs native document listeners). */
  const isEventInsideComponent = useCallback((e: MouseEvent) => {
    const path: unknown[] =
      typeof e.composedPath === 'function' ? e.composedPath() : [e.target];
    for (const node of path) {
      if (!(node instanceof Node)) continue;
      if (rootRef.current?.contains(node)) return true;
      if (node instanceof Element && node.closest?.(SEARCHABLE_MULTISELECT_MENU_SELECTOR)) {
        return true;
      }
    }
    const t = e.target;
    if (t instanceof Element) {
      if (rootRef.current?.contains(t)) return true;
      if (t.closest(SEARCHABLE_MULTISELECT_MENU_SELECTOR)) return true;
    }
    return false;
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const handlePointerOutside = (e: MouseEvent) => {
      if (isEventInsideComponent(e)) return;
      closeDropdown();
    };
    document.addEventListener('mousedown', handlePointerOutside);
    return () => document.removeEventListener('mousedown', handlePointerOutside);
  }, [isOpen, closeDropdown, isEventInsideComponent]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const handleWindowChange = () => updateMenuPosition();
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  const toggleValue = useCallback(
    (v: string) => {
      const cur = valuesRef.current;
      const next = new Set(cur);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChange(Array.from(next));
    },
    [onChange],
  );

  const removeValue = useCallback(
    (v: string) => {
      onChange(valuesRef.current.filter(x => x !== v));
    },
    [onChange],
  );

  const selectAllFiltered = useCallback(() => {
    const next = new Set(valuesRef.current);
    for (const opt of filteredOptions) {
      next.add(opt.value);
    }
    onChange(Array.from(next));
  }, [filteredOptions, onChange]);

  const dropdown =
    isOpen && !disabled ? (
      <ul
        ref={menuRef}
        id={listboxId}
        data-searchable-multiselect-menu=""
        role="listbox"
        aria-label={ariaLabel ?? 'Options'}
        aria-multiselectable="true"
        className="fixed z-[300] bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto py-1"
        style={
          menuRect
            ? {
                top: menuRect.top,
                left: menuRect.left,
                width: menuRect.width,
              }
            : undefined
        }
      >
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-sm text-muted-foreground">No results</li>
        ) : (
          <>
            {filteredOptions.length > 1 ? (
              <li className="sticky top-0 z-[1] border-b border-border bg-card px-2 py-1.5 shadow-sm">
                <button
                  type="button"
                  className="w-full rounded px-2 py-1.5 text-left text-xs font-medium text-primary hover:bg-muted/60"
                  onClick={e => {
                    e.stopPropagation();
                    selectAllFiltered();
                  }}
                >
                  Select all shown ({filteredOptions.length})
                </button>
              </li>
            ) : null}
            {filteredOptions.map(opt => {
              const checked = valueSet.has(opt.value);
              return (
                <li key={opt.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50',
                      checked && 'bg-primary/10',
                    )}
                    onClick={e => {
                      e.stopPropagation();
                      toggleValue(opt.value);
                    }}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-background',
                        checked && 'border-primary bg-primary/15',
                      )}
                      aria-hidden
                    >
                      {checked ? (
                        <span className="text-[11px] leading-none text-primary" aria-hidden>
                          ✓
                        </span>
                      ) : null}
                    </span>
                    <span className="truncate text-foreground">{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </>
        )}
      </ul>
    ) : null;

  return (
    <div ref={rootRef} className={cn('space-y-2', className)}>
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          value={isOpen ? searchTerm : values.length ? `${values.length} selected — type to search` : ''}
          onChange={e => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            updateMenuPosition();
          }}
          onFocus={() => {
            setIsOpen(true);
            updateMenuPosition();
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              closeDropdown();
              inputRef.current?.blur();
            }
          }}
          placeholder={values.length && !isOpen ? undefined : placeholder}
          disabled={disabled}
          className={cn(
            'w-full bg-background border border-border rounded-md text-sm text-foreground px-3 py-2 transition-colors focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
            isOpen && 'border-primary',
          )}
        />
      </div>

      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map(v => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-foreground shadow-sm dark:border-primary/40 dark:bg-primary/15"
            >
              <span className="max-w-[200px] truncate">
                {options.find(o => o.value === v)?.label ?? v}
              </span>
              {!disabled ? (
                <button
                  type="button"
                  className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-background/80 hover:text-destructive"
                  aria-label={`Remove ${v}`}
                  onClick={() => removeValue(v)}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}

      {typeof document !== 'undefined' && dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  );
}
